import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
  NgbCalendar,
  NgbDate,
  NgbDateAdapter,
  NgbDateStruct,
} from "@ng-bootstrap/ng-bootstrap";
import * as moment from "moment";
import { NgxSpinnerService } from "ngx-spinner";
import { DiscountPeriod } from "src/app/models/discount-period-model";
import { Simulator } from "src/app/models/simulator-model";
import { Studio } from "src/app/models/studio-model";
import { ReservationService } from "src/app/services/reservation/reservation.service";
import { StudioService } from "src/app/services/studio/studio.service";
import { Webflow } from "src/app/utils/webflow";

@Component({
  selector: "app-booking-form",
  templateUrl: "./booking-form.component.html",
  styleUrls: ["./booking-form.component.scss"],
})
export class BookingFormComponent implements OnInit {
  form!: FormGroup;
  loading = true;
  submitted = false;
  selectedStartDate: string = "--- ---"; // To hold the formatted start date
  selectedEndDate: string = "--- ---"; // To hold the formatted end date
  startDate: any | null = "--- ---";
  endDate: any | null = "--- ---";
  minDate: string = moment().format("YYYY-MM-DD"); // Preve nt past dates
  allHours: string[] = Array.from({ length: 24 }, (_, i) =>
    moment({ hour: i }).format("HH:00")
  );
  hours: string[] = [...this.allHours]; // Available hours for the selected date
  currentStudio: Studio | null = null;
  discount_periods: DiscountPeriod | null = null;
  params: any;

  ph: number = 0;
  nh: number = 0;
  final_price: number = 0;
  final_price_after_apply_promo: number = 0;
  reduce_value: string = "0";
  price_fees: any;

  promo_code_is_valid: boolean = false;
  promo_code_error: string = "";

  discount_periods_found: boolean = false;

  checking_promo_code: boolean = false;

  toDay: NgbDate | null;

  availableDiscounts: DiscountPeriod[] = [];
  availableDiscountRate: number | null = null;

  discountDays: Set<number> = new Set();

  // map the days with there values for possibility to work with theme in the ngbdatepicker
  dayMap: { [key: string]: number } = {
    sunday: 7,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  dropdownOpen = false;
  selectedHour: any = null;

  constructor(
    private spinner: NgxSpinnerService,
    private formBuilder: FormBuilder,
    private router: Router,
    private studioService: StudioService,
    private activatedRoute: ActivatedRoute,
    private reservationService: ReservationService,
    private calendar: NgbCalendar
  ) {
    this.form = this.formBuilder.group({
      startDate: ["", Validators.required],
      startDateMockup: ["", Validators.required],
      startTime: ["00:00", Validators.required],
      nbr_hours: [""],
      promo_code: [""],
    });
    this.toDay = calendar.getToday();
    this.form.get("startDateMockup")?.setValue(this.toDay); // Set default value (today)
  }

  //validator submit
  get formControl() {
    return this.form.controls;
  }

  ngOnInit(): void {
    this.loading = true;
    this.spinner.show();

    this.activatedRoute.queryParams.subscribe((params) => {
      this.params = params;
      this.studioService
        .get_one(params.studio)
        .subscribe(
          (studio) => {
            this.currentStudio = Studio.attributeDataToJson(studio);

            this.discountDays = new Set(
              this.currentStudio?.discount_periods?.map(
                (dp) => this.dayMap[dp.day_selected.toLowerCase()]
              )
            );
            this.form.patchValue({
              nbr_hours: this.currentStudio.nbr_min_hours,
            });

            const today = moment().format("YYYY-MM-DD"); // Format today’s date as 'YYYY-MM-DD'
            this.form.get("startDate")?.setValue(today); // Set default value
            this.updateAvailableTimes(today);
            this.get_discount_periods(this.calendar.getToday());

            // Watch for date selection changes
            this.form
              .get("startDateMockup")
              ?.valueChanges.subscribe((selectedDate) => {
                const formatted = this.ngbDateToString(selectedDate);
                this.form.get("startDate")?.setValue(formatted); // Set default value
                this.updateAvailableTimes(formatted);
                this.get_discount_periods(selectedDate);
              });

            this.setupValueChanges();
          },
          (error) => {
            this.currentStudio = null;
          }
        )
        .add(() => {
          setTimeout(() => {
            Webflow.setIFrameHeight();
            this.spinner.hide();
            this.loading = false;
          }, 500);
        });
    });
  }

  onSubmit() {
    this.submitted = true;
    // Dynamically update the min validator based on currentStudio's min hours
    const nbrHoursControl = this.form.get("nbr_hours");
    if (this.currentStudio?.nbr_min_hours && nbrHoursControl) {
      nbrHoursControl.setValidators([
        Validators.required,
        Validators.min(this.currentStudio.nbr_min_hours),
        Validators.max(23),
      ]);
      nbrHoursControl.updateValueAndValidity(); // Update the form validation state
    }
    if (this.form.invalid) {
      Webflow.setIFrameHeight();
      return;
    }

    // Convert to moment objects
    const startDateTime = moment(this.selectedStartDate, "DD-MM-YYYY HH:mm");
    const endDateTime = moment(this.selectedEndDate, "DD-MM-YYYY HH:mm");
    // Create the payload in the required format
    const data = {
      start_date: startDateTime.format("YYYY-MM-DD HH:mm"),
      end_date: endDateTime.format("YYYY-MM-DD HH:mm"),
      studio: this.currentStudio.id.toString(), // Replace with the actual studio ID if needed
    };

    if (this.promo_code_is_valid) {
      data["promo_code"] = this.form.get("promo_code")?.value;
    }

    // 1 - save data in localStorage
    localStorage.setItem("booking", JSON.stringify(data));
    // 2 - search if has any booking in this dates
    this.reservationService.busyIntervals(data).subscribe((res) => {
      // check if the innterval is free
      if (res) {
        // 3 - if the range is busy go to planning booking to select a new dates
        Webflow.myAction(this.router, "planning-booking", data);
      } else {
        // 4 - if the range is free redirect to payment page
        Webflow.myAction(this.router, "payment-form", data);
      }
    });
  }

  resetForm() {
    this.selectedStartDate = this.selectedEndDate = "--- ---";
    this.form.patchValue({
      startDate: "",
      startTime: "00:00",
      nbr_hours: this.currentStudio.nbr_min_hours,
    });
  }

  // Set up value changes to update selectedStartDate and selectedEndDate
  setupValueChanges() {
    let previousValues = this.form.value;

    this.form.valueChanges.subscribe((formValues) => {
      const { startDate, startTime, nbr_hours } = formValues;

      const startDateMoment = moment(
        `${startDate} ${startTime}`,
        "YYYY-MM-DD HH:mm"
      );
      const endDateMoment = startDateMoment.clone().add(nbr_hours, "hours");

      if (startDateMoment.isValid()) {
        this.selectedStartDate = startDateMoment.format("DD-MM-YYYY HH:mm");
        this.selectedEndDate = endDateMoment.format("DD-MM-YYYY HH:mm");
      }

      // Check if any relevant fields changed
      if (
        startDate !== previousValues.startDate ||
        startTime !== previousValues.startTime ||
        nbr_hours !== previousValues.nbr_hours
      ) {
        this.calculate_price();
      }

      previousValues = formValues;
    });
  }

  calculate_price() {
    const startDateTime = moment(this.selectedStartDate, "DD-MM-YYYY HH:mm");
    const endDateTime = moment(this.selectedEndDate, "DD-MM-YYYY HH:mm");

    var obj = {
      ph: this.currentStudio?.price,
      nh: Number(this.form.get("nbr_hours")?.value),
      studio: this.currentStudio.id.toString(),
      start_date: startDateTime.format("YYYY-MM-DD HH:mm"),
      end_date: endDateTime.format("YYYY-MM-DD HH:mm"),
    };

    if (this.promo_code_is_valid) {
      obj["promo_code"] = this.form.get("promo_code")?.value;
    }

    this.studioService.get_final_price(obj).subscribe((res) => {
      this.promo_code_error = "";
      this.final_price = res.final_price;
      this.final_price_after_apply_promo = res.final_price_after_apply_promo;
      this.promo_code_is_valid = res.promo_code_validity;
      this.discount_periods_found = res.discount_periods_found;
      this.reduce_value = (
        this.final_price - this.final_price_after_apply_promo
      )
        .toFixed(2)
        .toString();
      Webflow.setIFrameHeight();
    });
    this.studioService.get_price_fees().subscribe((res) => {
      this.price_fees = Simulator.attributeToJson(res);
    });
  }

  navigateTo(actionName: string) {
    Webflow.myAction(this.router, actionName, {});
  }

  check_promo_code() {
    const startDateTime = moment(this.selectedStartDate, "DD-MM-YYYY HH:mm");
    const endDateTime = moment(this.selectedEndDate, "DD-MM-YYYY HH:mm");

    this.promo_code_error = "";
    if (this.form.get("promo_code")?.value == "") {
      this.promo_code_error = "Veuillez saisir une code promotionnel";
      return;
    }

    this.checking_promo_code = true;

    var obj = {
      ph: this.currentStudio?.price,
      nh: Number(this.form.get("nbr_hours")?.value),
      promo_code: this.form.get("promo_code")?.value,
      studio: this.currentStudio.id.toString(),
      start_date: startDateTime.format("YYYY-MM-DD HH:mm"),
      end_date: endDateTime.format("YYYY-MM-DD HH:mm"),
    };
    this.studioService.get_final_price(obj).subscribe((res) => {
      if (res.promo_code_validity) {
        this.final_price = res.final_price;
        this.final_price_after_apply_promo = res.final_price_after_apply_promo;
        this.reduce_value = (
          this.final_price - this.final_price_after_apply_promo
        )
          .toFixed(2)
          .toString();
        this.promo_code_is_valid = true;
        this.discount_periods_found = res.discount_periods_found;
      } else {
        this.promo_code_error = "Le code promotionnel entré n'est pas valide";
        this.promo_code_is_valid = false;
      }
      this.checking_promo_code = false;
      Webflow.setIFrameHeight();
    });
  }

  dettach_promo_code() {
    this.promo_code_error = "";
    this.promo_code_is_valid = false;
    this.form.get("promo_code")?.setValue("");
    this.calculate_price();
    Webflow.setIFrameHeight();
  }

  updateAvailableTimes(selectedDate: string) {
    if (!selectedDate) {
      this.hours = [...this.allHours];
      return;
    }

    const selectedMoment = moment(selectedDate, "YYYY-MM-DD");

    const today = moment().startOf("day");
    const currentMoment = moment();
    const currentHour = currentMoment.hour();
    const currentMinutes = currentMoment.minutes();

    // Determine default hour: If minutes > 0, select next hour
    const defaultHour =
      currentMinutes > 0
        ? moment({ hour: currentHour + 1 }).format("HH:00")
        : moment({ hour: currentHour }).format("HH:00");

    if (selectedMoment.isSame(today, "day")) {
      // If today, exclude current hour if minutes > 0
      this.hours = this.allHours.filter((time) => {
        const hourValue = parseInt(time.split(":")[0], 10);
        return currentMinutes > 0
          ? hourValue > currentHour
          : hourValue >= currentHour;
      });
    } else {
      // If another day, show all hours from 00:00
      this.hours = [...this.allHours];
    }

    // Auto-select the default hour if it's available, otherwise select the first available hour
    setTimeout(() => {
      this.form
        .get("startTime")
        ?.setValue(
          this.hours.includes(defaultHour) ? defaultHour : this.hours[0]
        );
    }, 0);
  }

  get_discount_periods(selectedDate: any) {
    // 🔍 Get weekday number from NgbCalendar (1 = Monday, ..., 7 = Sunday)
    const weekday = this.calendar.getWeekday(selectedDate);

    // 🔄 Map that weekday number to the string like "monday", "tuesday", ...
    const discountDay = Object.entries(this.dayMap).find(
      ([_, value]) => value === weekday
    )?.[0]; // grab the key (like 'monday')

    // 🎯 Match against discount_periods
    if (discountDay) {
      this.availableDiscounts =
        this.currentStudio.discount_periods.filter(
          (dp) => dp.day_selected.toLowerCase() === discountDay
        ) || null;
    } else {
      this.availableDiscounts = [];
    }
  }

  ngbDateToString(date: NgbDateStruct): string {
    return moment({
      year: date.year,
      month: date.month - 1, // Moment uses 0-based months
      day: date.day,
    }).format("YYYY-MM-DD");
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  selectHour(hour: any) {
    this.selectedHour = hour;
    this.form.get("startTime").setValue(hour);
    this.dropdownOpen = false;
  }

  isDiscountDay = (date: NgbDate): boolean => {
    const day = this.calendar.getWeekday(date); // ISO day: 1=Mon, ..., 7=Sun
    return this.discountDays.has(day);
  };

  isHourInDiscount(hour: string): boolean {
    if (!this.availableDiscounts || this.availableDiscounts.length === 0)
      return false;

    const hourMoment = moment(hour, "HH:mm");

    return this.availableDiscounts.some((discount) => {
      const start = moment(discount.start_time, "HH:mm:ss.SSS");
      const end = moment(discount.end_time, "HH:mm:ss.SSS");
      return hourMoment.isSameOrAfter(start) && hourMoment.isBefore(end);
    });
  }

  getDiscountRateForHour(hour: string): number | null {
    const hourMoment = moment(hour, "HH:mm");

    const matching = this.availableDiscounts.find((discount) => {
      const start = moment(discount.start_time, "HH:mm:ss.SSS");
      const end = moment(discount.end_time, "HH:mm:ss.SSS");
      return hourMoment.isSameOrAfter(start) && hourMoment.isBefore(end);
    });

    return matching ? matching.discount_rate : null;
  }
}
